import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {PlusIcon} from '@primer/octicons-react'
import type {SelectPanelItemProps as ItemProps} from '@primer/react'
import {memo, useCallback, useMemo, useState} from 'react'

import type {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {MultiSelectValue} from '../../../api/columns/contracts/multi-select'
import type {NewOption} from '../../../api/columns/contracts/single-select'
import {replaceShortCodesWithEmojis} from '../../../helpers/emojis'
import {getInitialState, isOptionLimitReached} from '../../../helpers/initial-state'
import {emptySingleSelectOption} from '../../../helpers/new-column'
import {filterSuggestedSingleSelectOptions} from '../../../helpers/single-select-suggester'
import {getOptionMatchingFilterValue, type SuggestedSingleSelectOption} from '../../../helpers/suggestions'
import {useMultiSelectEditor} from '../../../hooks/editors/use-multi-select-editor'
import {convertOptionToItem, getName} from '../../../hooks/editors/use-single-select-editor'
import type {ColumnModelForDataType} from '../../../models/column-model'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import {FocusType} from '../../../navigation/types'
import {useFindColumn} from '../../../state-providers/columns/use-find-column'
import {useUpdateOptions} from '../../../state-providers/columns/use-update-options'
import {Resources} from '../../../strings'
import {SingleSelectOptionModal} from '../../fields/single-select/single-select-option-modal'
import {moveTableFocus, useStableTableNavigation} from '../navigation'
import {MultiSelectRenderer} from '../renderers/multi-select-renderer'
import styles from './multi-select-editor.module.css'
import {SelectPanelEditor, type TSelectPanelEditorProps} from './select-panel-editor'

type AdditionalProps = {
  currentValue: ColumnValue<MultiSelectValue>
  columnModel: ColumnModelForDataType<typeof MemexColumnDataType.MultiSelect>
}

const MultiSelectEditorFunc: React.FC<TSelectPanelEditorProps & AdditionalProps> = memo(
  ({model, columnModel, currentValue, rowIndex}) => {
    const {navigationDispatch} = useStableTableNavigation()
    const {addColumnOption} = useUpdateOptions()
    const {findColumn} = useFindColumn()
    const {
      projectLimits: {singleSelectColumnOptionsLimit},
    } = getInitialState()

    const selectedValueIds = useMemo(
      () => (hasValue(currentValue) ? currentValue.value.options.map(o => o.id) : []),
      [currentValue],
    )
    const {saveSelected, options, selected} = useMultiSelectEditor({
      columnModel,
      model,
      selectedValueIds,
    })
    const {isIssueField} = columnModel

    const renderButton = useCallback(() => {
      return <MultiSelectRenderer currentValue={currentValue} model={model} options={columnModel.settings.options} />
    }, [currentValue, model, columnModel.settings.options])

    const optionLimitReached = isOptionLimitReached(options)

    const [initialCreatingOption, setInitialCreatingOption] = useState<NewOption | null>(null)

    const onSaveNewOption = useCallback(
      async (option: NewOption) => {
        setInitialCreatingOption(null)

        await addColumnOption(columnModel, option)
        const column = findColumn(columnModel.id)
        // `addColumnOption` returns the full option set, not the newly created
        // entry. Find it by name and add it to the current multi-select
        // selection so the user's intent (create + select) is preserved.
        const addedOption =
          column?.dataType === columnModel.dataType
            ? column.settings.options.find(({name}) => name === option.name)
            : undefined

        if (addedOption) saveSelected([...selected, {...addedOption, selected: true}])

        navigationDispatch(moveTableFocus({focusType: FocusType.Focus}))
      },
      [addColumnOption, columnModel, findColumn, navigationDispatch, saveSelected, selected],
    )

    const renderCreateNewOption = useCallback(
      (optionName: string): ItemProps => {
        const sanitizedOptionName = replaceShortCodesWithEmojis(optionName)
        if (optionLimitReached) {
          return {
            ...testIdProps('table-cell-editor-max-options-length-row'),
            id: 'create-new-option',
            text: Resources.singleSelectOptionLimitWarning(singleSelectColumnOptionsLimit),
            disabled: true,
          }
        }

        return {
          id: 'create-new-option',
          description: 'Create new option',
          descriptionVariant: 'block',
          leadingVisual: PlusIcon,
          children: (
            <span className={styles.Text}>
              <strong>{sanitizedOptionName}</strong>
            </span>
          ),
          onAction: async (_, event) => {
            event.preventDefault()
            setInitialCreatingOption({...emptySingleSelectOption, name: sanitizedOptionName})
          },
          ...testIdProps('add-column-option'),
        }
      },
      [optionLimitReached, singleSelectColumnOptionsLimit],
    )

    const allOptionsGray = options.every(option => option.color === 'GRAY')

    const convertOption = useCallback(
      (option: SuggestedSingleSelectOption) => convertOptionToItem(option, !allOptionsGray),
      [allOptionsGray],
    )

    // Don't render the modal and the editor at the same time or the
    // click-outside handler will trigger on dialog clicks and close everything.
    return initialCreatingOption ? (
      <SingleSelectOptionModal
        initialOption={initialCreatingOption}
        onSave={onSaveNewOption}
        onCancel={() => setInitialCreatingOption(null)}
        noDuplicatedOptions={false}
        enableNameLengthValidation={false}
        disallowCommasInName={false}
      />
    ) : (
      <SelectPanelEditor<SuggestedSingleSelectOption>
        model={model}
        fetchOptions={noop}
        placeholderText="Filter options"
        selected={selected}
        options={options}
        filterOptions={filterSuggestedSingleSelectOptions}
        getSortAttribute={getName}
        convertOptionToItem={convertOption}
        saveSelected={saveSelected}
        renderButton={renderButton}
        renderCreateNewOption={isIssueField ? undefined : renderCreateNewOption}
        getOptionMatchingFilterValue={getOptionMatchingFilterValue}
        height="auto"
        maxHeight="large"
        width="medium"
        rowIndex={rowIndex}
        columnId={columnModel.id}
        isIssueField={isIssueField}
      />
    )
  },
)

MultiSelectEditorFunc.displayName = 'MultiSelectEditor'

export const MultiSelectEditor = memo(MultiSelectEditorFunc)
